/**
 * How long a load may run before its skeleton is drawn at all.
 *
 * Most loads a member triggers finish inside this: a revisit served from the
 * cache, and a screen query composed from endpoint entries another screen
 * already filled (`api/endpointCache.ts`) — which React Query still delivers
 * a frame late, because its notifications are batched on `setTimeout(0)`.
 * Drawing a skeleton for that one frame is the grey flash this exists to
 * stop. The wait itself is CSS (`--motion-skeleton-delay`, the delay inside
 * `animate-skeleton-in`); this copy only tells a script when the skeleton has
 * painted, which decides whether the content after it crosses it
 * (useSkeletonTiming) and whether a pending page's blocks are worth fading
 * out over its loaded page (page-frame.ts). A route's pending page and a card
 * inside it wait the same 80ms, so they make a member wait identically.
 */
export const SKELETON_DELAY_MS = 80

/**
 * How long a skeleton and the content replacing it cross in one box: the
 * content's 220ms fade-in (`--motion-med`), the longer of the two. The
 * skeleton's own fade-out (`--motion-skeleton-out`, 160ms) ends inside it, so
 * when this is up both have finished and nothing is cut short by dropping
 * their classes.
 */
export const SKELETON_SWAP_MS = 220

/** One shimmer cycle, sweep and rest: `--motion-shimmer`. */
export const SHIMMER_CYCLE_MS = 2250

/** The keyframes `--animate-shimmer` runs (globals.css). */
const SHIMMER_ANIMATION = 'shimmer'

const isShimmer = (animation: Animation): boolean =>
  'animationName' in animation && (animation as CSSAnimation).animationName === SHIMMER_ANIMATION

let queued = false

/**
 * Puts every skeleton's shimmer on one clock, the document's: each sweep
 * starts on the same beat, so a card whose skeleton mounts 300ms after the
 * page's sweeps with it instead of a beat behind, and a pending page's
 * blocks copied over its loaded page (page-frame.ts) carry on from where the
 * originals were rather than starting their sweep again.
 *
 * A CSS animation starts when its element is first styled, so this sets
 * each new shimmer's start time to the timeline's origin. Once per frame
 * however many blocks mount: in the next animation frame, ahead of that
 * frame's own style pass and paint, so no block is ever drawn on its own beat
 * first. `getAnimations()` brings style up to date to answer — work the frame
 * was about to do anyway, where a microtask would have done it inside the
 * commit that mounted the blocks and lengthened that task by as much (18ms of
 * a page's reveal at 4x CPU, measured).
 *
 * Nothing to do where there are no Web Animations (jsdom), and nothing
 * under reduced motion, where the highlight is not drawn at all.
 */
export function syncShimmer(): void {
  if (queued || typeof document.getAnimations !== 'function') return
  queued = true
  requestAnimationFrame(() => {
    queued = false
    for (const animation of document.getAnimations()) {
      if (isShimmer(animation) && animation.startTime !== 0) animation.startTime = 0
    }
  })
}
